// contexts/voice/AudioStreamer.ts
// Audio streaming class - based on Google's official live-api-web-console implementation
// https://github.com/google-gemini/live-api-web-console/blob/main/src/lib/audio-streamer.ts

import { VOICE_CONSTANTS } from './constants';

export class AudioStreamer {
  private sampleRate: number = VOICE_CONSTANTS.AUDIO_SAMPLE_RATE_OUTPUT;
  private bufferSize: number = VOICE_CONSTANTS.STREAMER_BUFFER_SIZE;
  private audioQueue: Float32Array[] = [];
  private isPlaying: boolean = false;
  private isStreamComplete: boolean = false;
  private checkInterval: number | null = null;
  private scheduledTime: number = 0;
  private initialBufferTime: number = VOICE_CONSTANTS.STREAMER_INITIAL_BUFFER_TIME;
  public gainNode: GainNode;
  private endOfQueueAudioSource: AudioBufferSourceNode | null = null;

  // Tracks samples actually committed to the Web Audio API (dequeued and scheduled).
  // Auto-resets when new audio arrives after stop() or complete().
  // Used to report exactly how much agent audio the user heard per turn.
  private samplesScheduled: number = 0;
  private needsCounterReset: boolean = true;

  public onComplete = () => {};

  constructor(public context: AudioContext) {
    this.gainNode = this.context.createGain();
    this.gainNode.connect(this.context.destination);
  }

  private _processPCM16Chunk(chunk: Uint8Array): Float32Array {
    const float32Array = new Float32Array(chunk.length / 2);
    const dataView = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);
    for (let i = 0; i < chunk.length / 2; i++) {
      try {
        const int16 = dataView.getInt16(i * 2, true);
        float32Array[i] = int16 / 32768;
      } catch (e) {
        console.error(e);
      }
    }
    return float32Array;
  }

  addPCM16(chunk: Uint8Array) {
    // Auto-reset counter at start of new turn (after stop/complete)
    if (this.needsCounterReset) {
      this.samplesScheduled = 0;
      this.needsCounterReset = false;
    }
    this.isStreamComplete = false;
    let processingBuffer = this._processPCM16Chunk(chunk);

    while (processingBuffer.length >= this.bufferSize) {
      const buffer = processingBuffer.slice(0, this.bufferSize);
      this.audioQueue.push(buffer);
      processingBuffer = processingBuffer.slice(this.bufferSize);
    }

    if (processingBuffer.length > 0) {
      this.audioQueue.push(processingBuffer);
    }

    if (!this.isPlaying) {
      this.isPlaying = true;
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
      this.scheduleNextBuffer();
    }
  }

  // Convenience method for base64 encoded audio
  play(base64Audio: string): void {
    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    this.addPCM16(bytes);
  }

  private createAudioBuffer(audioData: Float32Array): AudioBuffer {
    const audioBuffer = this.context.createBuffer(1, audioData.length, this.sampleRate);
    audioBuffer.getChannelData(0).set(audioData);
    return audioBuffer;
  }

  private scheduleNextBuffer() {
    const SCHEDULE_AHEAD_TIME = VOICE_CONSTANTS.STREAMER_SCHEDULE_AHEAD_TIME;

    while (
      this.audioQueue.length > 0 &&
      this.scheduledTime < this.context.currentTime + SCHEDULE_AHEAD_TIME
    ) {
      const audioData = this.audioQueue.shift()!;
      this.samplesScheduled += audioData.length;
      const audioBuffer = this.createAudioBuffer(audioData);
      const source = this.context.createBufferSource();

      if (this.audioQueue.length === 0) {
        if (this.endOfQueueAudioSource) {
          this.endOfQueueAudioSource.onended = null;
        }
        this.endOfQueueAudioSource = source;
        source.onended = () => {
          if (!this.audioQueue.length && this.endOfQueueAudioSource === source) {
            this.endOfQueueAudioSource = null;
            this.onComplete();
          }
        };
      }

      source.buffer = audioBuffer;
      source.connect(this.gainNode);

      const startTime = Math.max(this.scheduledTime, this.context.currentTime);
      source.start(startTime);
      this.scheduledTime = startTime + audioBuffer.duration;
    }

    if (this.audioQueue.length === 0) {
      if (this.isStreamComplete) {
        this.isPlaying = false;
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
          this.checkInterval = null;
        }
      } else {
        if (!this.checkInterval) {
          this.checkInterval = window.setInterval(() => {
            if (this.audioQueue.length > 0) {
              this.scheduleNextBuffer();
            }
          }, VOICE_CONSTANTS.STREAMER_CHECK_INTERVAL) as unknown as number;
        }
      }
    } else {
      const nextCheckTime = (this.scheduledTime - this.context.currentTime) * 1000;
      setTimeout(() => this.scheduleNextBuffer(), Math.max(0, nextCheckTime - 50));
    }
  }

  /**
   * How many PCM16 bytes (24kHz) were actually scheduled for playback
   * since the last resetTracking() call. This is exact — only counts
   * audio committed to the Web Audio API, not queued-but-unplayed data.
   */
  getPlayedBytes(): number {
    return this.samplesScheduled * 2;  // Float32 samples → PCM16 bytes
  }

  /**
   * Reset the playback tracking counter. Call when a new agent turn
   * starts (on 'done') or after reporting played bytes (on 'interrupted').
   */
  resetTracking(): void {
    this.samplesScheduled = 0;
  }

  stop() {
    this.isPlaying = false;
    this.isStreamComplete = true;
    this.needsCounterReset = true;
    this.audioQueue = [];
    this.scheduledTime = this.context.currentTime;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.gainNode.gain.linearRampToValueAtTime(0, this.context.currentTime + VOICE_CONSTANTS.STREAMER_FADE_TIME);

    setTimeout(() => {
      this.gainNode.disconnect();
      this.gainNode = this.context.createGain();
      this.gainNode.connect(this.context.destination);
    }, 200);
  }

  async resume() {
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }
    this.isStreamComplete = false;
    this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    this.gainNode.gain.setValueAtTime(1, this.context.currentTime);
  }

  complete() {
    this.isStreamComplete = true;
    this.needsCounterReset = true;
  }
}
